import React, { useState, useCallback } from 'react';
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row } from 'reactstrap';
import SimpleBar from "simplebar-react";
import axios from 'axios';
import { api } from '../../config';

const BASE_URL = process.env.REACT_APP_CHATBOT_API_URL || '';

const SEVERITY_CONFIG = {
    ALERT: {
        icon: 'bx bxs-error-circle',
        badgeClass: 'bg-danger',
        badgeText: 'Alert',
        bgClass: 'bg-danger-subtle',
        textClass: 'text-danger',
        borderClass: 'border-danger',
    },
    WARNING: {
        icon: 'bx bxs-error',
        badgeClass: 'bg-warning',
        badgeText: 'Warning',
        bgClass: 'bg-warning-subtle',
        textClass: 'text-warning',
        borderClass: 'border-warning',
    },
};

const NotificationDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    const fetchAlerts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const tenantId = localStorage.getItem('tenant_id') || 1;
            const response = await axios.get(`${BASE_URL}/inventory-alerts`, {
                params: { tenant_id: tenantId },
            });
            // axios intercepter unwraps .data, but handle both cases
            const payload = response?.result ? response : response?.data;
            setData(payload?.result ?? response?.result ?? null);
        } catch (err) {
            setError('Failed to load alerts.');
        } finally {
            setLoading(false);
            setHasFetched(true);
        }
    }, []);

    // Fetch on first open
    const toggle = () => {
        if (!isOpen && !hasFetched) {
            fetchAlerts();
        }
        setIsOpen(prev => !prev);
    };

    const totalIssues = data?.total_issues ?? 0;
    const alertsCount = data?.alerts_count ?? 0;
    const warningsCount = data?.warnings_count ?? 0;
    const issues = data?.issues ?? [];

    return (
        <React.Fragment>
            <Dropdown isOpen={isOpen} toggle={toggle} className="topbar-head-dropdown ms-1 header-item">
                <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle position-relative">
                    <i className='bx bx-bell fs-22'></i>
                    {totalIssues > 0 && (
                        <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
                            {totalIssues}
                            <span className="visually-hidden">inventory alerts</span>
                        </span>
                    )}
                </DropdownToggle>

                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0" style={{ minWidth: '360px' }}>
                    {/* Header */}
                    <div className="dropdown-head bg-primary bg-pattern rounded-top p-3">
                        <Row className="align-items-center">
                            <Col>
                                <h6 className="m-0 fs-16 fw-semibold text-white">
                                    <i className="bx bx-package me-2"></i>Inventory Alerts
                                </h6>
                                {data?.checked_at && (
                                    <small className="text-white-50">
                                        Last checked: {data.checked_at}
                                    </small>
                                )}
                            </Col>
                            <div className="col-auto">
                                {totalIssues > 0 ? (
                                    <span className="badge bg-danger-subtle text-danger fs-12 fw-semibold px-2">
                                        {totalIssues} Issue{totalIssues !== 1 ? 's' : ''}
                                    </span>
                                ) : (
                                    <span className="badge bg-success-subtle text-success fs-12 fw-semibold px-2">
                                        All Clear
                                    </span>
                                )}
                            </div>
                        </Row>

                        {/* Summary Badges */}
                        {data && (
                            <div className="mt-2 d-flex gap-2 flex-wrap">
                                <span className="badge bg-danger fs-11">
                                    <i className="bx bxs-error-circle me-1"></i>
                                    {alertsCount} Alert{alertsCount !== 1 ? 's' : ''}
                                </span>
                                <span className="badge bg-warning text-dark fs-11">
                                    <i className="bx bxs-error me-1"></i>
                                    {warningsCount} Warning{warningsCount !== 1 ? 's' : ''}
                                </span>
                                <span className="badge bg-success fs-11">
                                    <i className="bx bx-check-circle me-1"></i>
                                    {data.healthy_count ?? 0} Healthy
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Body */}
                    <SimpleBar style={{ maxHeight: '380px' }}>
                        {loading && (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted mt-2 mb-0 fs-13">Checking inventory...</p>
                            </div>
                        )}

                        {error && !loading && (
                            <div className="text-center py-4 px-3">
                                <i className="bx bx-wifi-off fs-36 text-muted"></i>
                                <p className="text-muted mt-2 mb-0 fs-13">{error}</p>
                                <button
                                    className="btn btn-sm btn-soft-primary mt-2"
                                    onClick={fetchAlerts}
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {!loading && !error && issues.length === 0 && hasFetched && (
                            <div className="text-center py-4 px-3">
                                <i className="bx bx-check-shield fs-40 text-success"></i>
                                <h6 className="mt-2 mb-1 fs-15 text-success fw-semibold">All Good!</h6>
                                <p className="text-muted fs-13 mb-0">No inventory issues found.</p>
                                {data?.total_products_checked && (
                                    <small className="text-muted">{data.total_products_checked} products checked.</small>
                                )}
                            </div>
                        )}

                        {!loading && !error && issues.length > 0 && (
                            <div className="p-2">
                                {/* AI Summary */}
                                {data?.ai_summary && (
                                    <div className="alert alert-info d-flex gap-2 py-2 px-3 mb-2 border-0 rounded-3"
                                        style={{ fontSize: '12px', background: 'rgba(var(--vz-info-rgb), 0.1)' }}>
                                        <i className="bx bx-bot fs-16 text-info flex-shrink-0 mt-1"></i>
                                        <span className="text-muted">{data.ai_summary}</span>
                                    </div>
                                )}

                                {/* Issue Cards */}
                                {issues.map((issue, idx) => {
                                    const config = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.WARNING;
                                    return (
                                        <div
                                            key={issue.product_id ?? idx}
                                            className={`d-flex gap-3 p-2 mb-2 rounded-3 border-start border-3 ${config.borderClass}`}
                                            style={{ background: 'var(--vz-card-bg, #fff)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
                                        >
                                            {/* Icon */}
                                            <div className={`flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle ${config.bgClass}`}
                                                style={{ width: 38, height: 38 }}>
                                                <i className={`${config.icon} fs-18 ${config.textClass}`}></i>
                                            </div>

                                            {/* Info */}
                                            <div className="flex-grow-1 overflow-hidden">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <p className="mb-0 fw-semibold fs-13 text-truncate" style={{ maxWidth: '200px' }}>
                                                        {issue.product_name}
                                                    </p>
                                                    <span className={`badge ${config.badgeClass} ms-1 flex-shrink-0 fs-10`}>
                                                        {config.badgeText}
                                                    </span>
                                                </div>
                                                <div className="d-flex gap-3 mt-1 flex-wrap">
                                                    <span className="d-flex align-items-center gap-1 fs-11 text-muted">
                                                        <i className="bx bx-box text-primary"></i>
                                                        Stock: <strong className={issue.current_stock <= 0 ? 'text-danger' : 'text-dark'}>
                                                            {issue.current_stock}
                                                        </strong>
                                                    </span>
                                                    {issue.pending_qty > 0 && (
                                                        <span className="d-flex align-items-center gap-1 fs-11 text-muted">
                                                            <i className="bx bx-time-five text-warning"></i>
                                                            Pending: <strong className="text-warning">{issue.pending_qty}</strong>
                                                        </span>
                                                    )}
                                                    {issue.post_fulfillment_stock < 0 && (
                                                        <span className="d-flex align-items-center gap-1 fs-11 text-muted">
                                                            <i className="bx bx-trending-down text-danger"></i>
                                                            After: <strong className="text-danger">{issue.post_fulfillment_stock}</strong>
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mb-0 mt-1 fs-11 text-muted text-truncate" title={issue.message}>
                                                    {issue.message}
                                                </p>
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    <span className="text-muted fs-11">
                                                        <i className="bx bx-barcode-reader me-1"></i>{issue.sku}
                                                    </span>
                                                    {issue.recommended_reorder_qty > 0 && (
                                                        <span className="badge bg-info-subtle text-info fs-10">
                                                            Reorder: {issue.recommended_reorder_qty} units
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </SimpleBar>

                    {/* Footer */}
                    {hasFetched && !loading && (
                        <div className="p-2 border-top d-flex justify-content-between align-items-center">
                            <small className="text-muted fs-11">
                                {data?.total_products_checked
                                    ? `${data.total_products_checked} products checked`
                                    : ''}
                            </small>
                            <button
                                className="btn btn-sm btn-soft-secondary py-0 px-2 fs-11"
                                onClick={fetchAlerts}
                                disabled={loading}
                            >
                                <i className="bx bx-refresh me-1"></i>Refresh
                            </button>
                        </div>
                    )}
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default NotificationDropdown;